export type PrescriptionNumberFields = {
  numero_prescrizione?: string | null;
  external_number_text?: string | null;
};

function clean(value: string | null | undefined): string {
  return value?.trim() ?? "";
}

/** Search aliases only: never rewrite the identifier received from PrimoUp. */
export function prescriptionNumberSearchTerms(value: string): string[] {
  const term = value.trim().toUpperCase();
  if (!term) return [];
  const match = /^(\d+)\s*([A-Z][A-Z0-9]*)?$/.exec(term);
  if (!match) return [term];

  const digits = match[1].replace(/^0+(?=\d)/, "");
  const suffix = match[2] ?? "";
  const terms = new Set([term, `${digits}${suffix}`]);
  // Local legacy references use four digits; PrimoUp references may use six.
  // Search each padding width without changing the stored/displayed number.
  for (let width = digits.length; width <= 6; width++) {
    terms.add(`${digits.padStart(width, "0")}${suffix}`);
  }
  return [...terms];
}

/** Numero PrimoUp/PrimoLab, disponibile dopo la sincronizzazione. */
export function externalPrescriptionNumber(
  prescription: PrescriptionNumberFields
): string | undefined {
  return clean(prescription.external_number_text) || undefined;
}

/** Numero tecnico generato da PrimoFastLab prima della sincronizzazione. */
export function internalPrescriptionNumber(
  prescription: PrescriptionNumberFields
): string | undefined {
  return clean(prescription.numero_prescrizione) || undefined;
}

/**
 * Riferimento da mostrare nell'interfaccia e nelle notifiche: PrimoUp quando
 * disponibile, altrimenti il numero interno con un'etichetta non ambigua.
 */
export function prescriptionDisplayNumber(
  prescription: PrescriptionNumberFields,
  internalPrefix = "Interno"
): string {
  const external = externalPrescriptionNumber(prescription);
  if (external) return external;

  const internal = internalPrescriptionNumber(prescription);
  if (!internal) return "—";
  return internalPrefix ? `${internalPrefix} ${internal}` : internal;
}

/** Valore compatto per nomi file o riferimenti tecnici non etichettati. */
export function prescriptionNumberValue(
  prescription: PrescriptionNumberFields
): string {
  return (
    externalPrescriptionNumber(prescription) ??
    internalPrescriptionNumber(prescription) ??
    "prescrizione"
  );
}

/** Testo esplicito per le sole email di approvazione del salto fase. */
export function internalPrescriptionEmailReference(
  prescription: PrescriptionNumberFields
): string {
  const internal = internalPrescriptionNumber(prescription);
  return internal
    ? `Interno PrimoFastLab ${internal}`
    : "Interno PrimoFastLab non disponibile";
}
